/**
 * @file use-agent-revoke.ts
 * @description Состояние экстренного отзыва ключа агента на странице бэкапов
 * (phase19.26 шаг 7.1, Р2; TD-161).
 *
 * - `revokeNonce` — счётчик отзывов: команда установки, полученная до отзыва,
 *   погашена сервером, блок установки убирает её с экрана;
 * - `agentRevoked` — показывать ли плашку «Ключ отозван»: с момента отзыва
 *   и до тех пор, пока агент не отчитается снова (после повторной установки).
 *   Снимок времени последнего отчёта берём из данных, перечитанных сразу после
 *   отзыва (`reload`); перечитать не удалось — из данных на экране.
 */

import { useCallback, useState } from "react";

import { agentReportedSince } from "./backup-agent-activity";

/** null — отзыва не было; "pending" — ждём перечитывания; иначе снимок. */
type Snapshot = null | "pending" | { mark: number | null };

/**
 * @param current время последнего отчёта агента по данным на экране
 * @param reload перечитать данные страницы; вернуть время последнего отчёта
 *   или `undefined`, если перечитать не удалось
 */
export function useAgentRevoke(current: number | null, reload: () => Promise<number | null | undefined>) {
  const [revokeNonce, setRevokeNonce] = useState(0);
  const [snapshot, setSnapshot] = useState<Snapshot>(null);

  const onRevoked = useCallback(async () => {
    setRevokeNonce((n) => n + 1);
    setSnapshot("pending");
    const fresh = await reload();
    setSnapshot({ mark: fresh === undefined ? current : fresh });
  }, [current, reload]);

  const agentRevoked =
    snapshot !== null && (snapshot === "pending" || !agentReportedSince(snapshot.mark, current));

  return { revokeNonce, agentRevoked, onRevoked };
}
